import { Box, Theme, Typography, List, ListItem } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useEffect, useState } from 'react';
import { Link, Loader } from '../../../components';
import { IPunchlistTerritory } from '../../../models';
import { getPunchlistTerritories } from '../../../fetch';
import { alphaSort } from '../../../helpers';

interface INeighborhoodList {
  setNeighborhoodId: (neighborhoodId: number) => void;
  setIsNeighborhoodSearch: (search: boolean) => void;
}

export const NeighborhoodList: FC<INeighborhoodList> = ({
  setNeighborhoodId,
  setIsNeighborhoodSearch
}) => {
  const classes = useStyles();
  const [isLoadingNeighborhoods, setLoadingNeighborhoods] = useState<boolean>(true);
  const [neighborhoods, setNeighborhoods] = useState<IPunchlistTerritory[]>([]);

  const fetchNeighborhoods = async () => {
    try {
      setLoadingNeighborhoods(true);
      const res = await getPunchlistTerritories();
      const sortByTerritories = alphaSort(res, 'territoryName');
      const sortByNeightborhoods = sortByTerritories.map(item => {
        const sortedNeighborhoods = alphaSort(item.neighborhoods, 'neighborhoodName');
        return { ...item, neighborhoods: sortedNeighborhoods };
      });
      setNeighborhoods(sortByNeightborhoods);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingNeighborhoods(false);
    }
  };

  useEffect(() => {
    fetchNeighborhoods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <List
        component="nav"
        aria-labelledby="neighborhood-list-header"
        subheader={
          <Typography
            color="primary"
            variant="h5"
            id="neighborhood-list-header"
            sx={{ marginBottom: theme => theme.spacing(0.5) }}
          >
            Neighborhoods
          </Typography>
        }
      >
        {!isLoadingNeighborhoods && (neighborhoods?.length === 0 || !neighborhoods) && (
          <ListItem dense disablePadding>
            <Typography>There are no neighborhood punch lists to display.</Typography>
          </ListItem>
        )}
        {!isLoadingNeighborhoods && neighborhoods && neighborhoods?.length > 0 && (
          <>
            {neighborhoods.map((territory, territoryIndex) => {
              return (
                <ListItem
                  dense
                  disablePadding
                  key={`${territory.territoryId}-${territory.territoryName}`}
                >
                  <List
                    className={classes.subList}
                    disablePadding
                    aria-labelledby={`territory-list-subheader-${territoryIndex}`}
                    subheader={
                      <Typography color="secondary" variant="h6" className={classes.subHeader}>
                        {territory.territoryName}
                      </Typography>
                    }
                  >
                    {territory.neighborhoods.map((neighborhood, neighborhoodIndex) => {
                      return (
                        <ListItem
                          key={`${neighborhood.neighborhoodId}-${neighborhood.neighborhoodName}`}
                          component={Link}
                          to="#"
                          onClick={() => {
                            setNeighborhoodId(neighborhood.neighborhoodId);
                            setIsNeighborhoodSearch(true);
                          }}
                          className={classes.listItem}
                        >
                          {neighborhood.neighborhoodName}
                        </ListItem>
                      );
                    })}
                  </List>
                </ListItem>
              );
            })}
          </>
        )}
      </List>
      {isLoadingNeighborhoods && (
        <Box>
          <Loader position="left" type="inline" />
        </Box>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  subList: {
    display: 'block',
    width: '100%',
    marginTop: theme.spacing(0.5)
  },
  subHeader: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    display: 'block',
    marginBottom: theme.spacing(0.5)
  },
  listItem: { paddingTop: theme.spacing(0.25), paddingBottom: theme.spacing(0.25) }
}));
